import React, { useEffect, useState } from 'react';
import { QRCodeScanner } from '../../electron/serialport';
import { IAppointment } from '../API/GoMoto';
import { StormApiOpenLocker, STORM_API_FILE, StormApiReadLockerStatus, LockerStatus } from '../helpers/stormExpress';
import { useKioskState } from './Kiosk/kiosk';
import { ILockerFixture, useLockerContext, useSetLockerStatus } from './lockers';
import { ILicenseScanResult, IQRCodeScanResult, IScanError, QRCodeData, ScanErrorCode } from './mzero';
import dayjs from 'dayjs';
import { isDevelopment } from '../helpers/EnvironmentFlags/EnvironmentFlags';
import { GoMotoFunction } from '../API/GoMoto.types';

type StormProviderProps = { children: React.ReactNode; };

type InitialStateType = {
    connected?: boolean;
};

// Should line up with definition in electron/serialPort
enum ScannerType {
    Unknown = 0,
    Newland,
    Honeywell
}

const initialState = {};
const StormStateContext = React.createContext<InitialStateType>(initialState);
export function useStormState() {
    return React.useContext(StormStateContext);
}

export function StormProvider({ children }: StormProviderProps) {
    const [state, setState] = React.useState<InitialStateType>({ connected: false });

    useEffect(() => {
        const fs = window.fs;
        setState({ connected: fs.existsSync(STORM_API_FILE) });
    }, []);

    return <StormStateContext.Provider value={state}>{children}</StormStateContext.Provider>;
}

export const useStormLocker = (onLockerOpen?: GoMotoFunction<[void], void>) => {
    const [error, setError] = useState<string>();
    const { connected } = useStormState();
    const { keyStorageType } = useKioskState();
    const { loading, lockers } = useLockerContext();
    const setLockerStatus = useSetLockerStatus();
    const cancel = React.useRef<boolean>(false);

    const waitForClose = async (id: number) => {
        const waitUntil: dayjs.Dayjs = dayjs()
            .add(1800, 'second');

        while (dayjs()
            .isBefore(waitUntil)) {
            if (cancel.current) {
                break;
            }

            const status: LockerStatus = await StormApiReadLockerStatus(id);

            if (status === LockerStatus.Unknown || status === LockerStatus.Closed) {
                if (status === LockerStatus.Closed) {
                    console.log(`door ${id} closed`);
                }
                return true;
            }
        }

        return false;
    };

    useEffect(() => () => {
        cancel.current = true;
    }, []);

    const storeInLocker = (id: number) =>
        new Promise<void>((resolve, reject) => {
            const locker = lockers.find((l) => l.Id === id);
            if (locker?.Status === 'Available') {
                console.log(`Opening locker ${id}`);
                if (isDevelopment()) {
                    setLockerStatus(locker, 'Taken');
                    resolve();
                }
                else {
                    StormApiOpenLocker(id)
                        .then(async () => {
                            if (!(await waitForClose(id))) {
                                reject('TimeOut');
                            }
                            else {
                                setLockerStatus(locker, 'Taken');
                                resolve();
                            }
                        })
                        .catch((message: string) => {
                            setError(message);
                            reject(message);
                        });
                }
            }
            else {
                reject(false);
            }
        });

    const retrieveFromLocker = (id: ILockerFixture['Id'], appointmentKey?: IAppointment['key'], onOpen?: GoMotoFunction<[void], void>) =>
        new Promise<ILockerFixture | false>((resolve, reject) => {
            const locker = lockers.find((l) => l.Id === id);
            if (locker?.Status === 'Taken') {
                console.log(`Opening locker ${id}`);
                if (isDevelopment()) {
                    onOpen?.();
                    setLockerStatus(locker, 'Available');
                    resolve(locker);
                }
                else {
                    StormApiOpenLocker(id)
                        .then(async () => {
                            onOpen?.();

                            if (!(await waitForClose(id))) {
                                reject('TimeOut');
                            }
                            else {
                                setLockerStatus(locker, 'Available');
                                resolve(locker);
                            }
                        })
                        .catch((message: string) => {
                            setError(message);
                            reject(message);
                        });
                }
            }
            else {
                resolve(false);
            }
        });

    const openLocker = (id: ILockerFixture['Id']) =>
        new Promise<ILockerFixture | false>((resolve, reject) => {
            const locker: ILockerFixture | undefined = lockers.find((locker: ILockerFixture) => locker.Id === id);
            if (locker) {
                console.log(`Opening locker ${id}`);
                StormApiOpenLocker(id)
                    .then(async () => {
                        onLockerOpen?.();

                        if (!(await waitForClose(id))) {
                            reject('TimeOut');
                        }
                        else {
                            resolve(locker);
                        }
                    })
                    .catch((message: string) => {
                        setError(message);
                        reject(message);
                    });
            }
            else {
                resolve(false);
            }
        });
    // TODO: appointment key remove?
    const exchangeKeyAtLocker = (id: ILockerFixture['Id'], appointmentKey?: IAppointment['key'], onOpen?: GoMotoFunction<[void], void>) => {
        return new Promise<ILockerFixture | false>((resolve, reject) => {
            const locker: ILockerFixture | undefined = lockers.find((locker: ILockerFixture) => locker.Id === id);

            if (locker?.Status === 'Taken') {
                console.log(`Opening locker ${id}`);
                if (isDevelopment()) {
                    onOpen?.();
                    resolve(locker);
                }
                else {
                    StormApiOpenLocker(id)
                        .then(async () => {
                            onOpen?.();

                            if (!(await waitForClose(id))) {
                                reject('TimeOut');
                            }
                            else {
                                resolve(locker);
                            }
                        })
                        .catch((message: string) => {
                            setError(message);
                            reject(message);
                        });
                }
            }
            else {
                resolve(false);
            }
        });
    };

    return {
        loading: loading || !connected,
        /** Stores a key in the provided locker. */
        storeInLocker,
        /** Opens a locker for a key to be retrieved */
        retrieveFromLocker,
        openLocker,
        exchangeKeyAtLocker,
        error,
        lockersEnabled: !!connected && !!keyStorageType,
    };
};

const useStormScan = () => {
    const [error, setError] = React.useState<IScanError>();
    const [inProgress, setInProgress] = React.useState(false);
    const [result, setResult] = React.useState<string>();
    const [scannerType, setScannerType] = React.useState<ScannerType>(ScannerType.Unknown);
    const scanner = React.useRef<QRCodeScanner>();

    /**
     * Turns on/off the scanner based on the flag state.
     */
    useEffect(() => {
        if (scanner.current) {
            console.log(inProgress ? 'starting serial com' : 'stopping serial com');
            if (inProgress) {
                scanner.current.StartScanning(onScan, onError);
            }
            else {
                scanner.current.StopScanning();
            }
        }
    }, [inProgress, scanner]);

    /**
     * Stop the scanner if it is currently scanning when unmounting
     * the component
     */
    useEffect(
        () => () => {
            if (scanner.current) {
                scanner.current.StopScanning();
            }
        },
        [],
    );

    /**
     * Callback for scan results
     *
     * @param result the scanned data
     */
    const onScan = (result: string, scannerType: ScannerType) => {
        setResult(result);
        setInProgress(false);
        setScannerType(scannerType);
    };

    /**
     * Callback for error events
     *
     * @param code the error code (defined in electron/serialport)
     * @param message any error message
     */
    const onError = (code: number, message?: string) => {
        console.log(`scanner error - ${code} - ${message}`);
        switch (code) {
            case 1:
            case 2:
                setError({
                    message,
                    code: ScanErrorCode.scannerNotFound,
                });
                break;
            case 3:
                setError({
                    message,
                    code: ScanErrorCode.badData,
                });
                break;
            case 4:
                setError({
                    message,
                    code: ScanErrorCode.timeout,
                });
                break;
        }
        setInProgress(false);
    };

    /**
     * Initializes the scanner object and sets a flag to start scanning
     */
    const start = () => {
        if (!inProgress) {
            setInProgress(true);
            scanner.current = window.QRCodeScanner();
        }
    };

    /**
     * Sets a flag to stop scanning
     */
    const stop = () => {
        if (inProgress) {
            setInProgress(false);
        }
    };

    return {
        error,
        setError,
        result,
        start,
        stop,
        inProgress,
        clearResult: () => {
            setResult(undefined);
        },
        reset: () => {
            setError(undefined);
            setResult(undefined);
            stop();
        },
        simulateError:
            (isDevelopment() &&
                ((errorCode: ScanErrorCode) => {
                    setError({ code: errorCode });
                    setResult(undefined);
                    setInProgress(false);
                })) ||
            undefined,
        simulateResult:
            (isDevelopment() &&
                ((result: string) => {
                    setError(undefined);
                    setResult(result);
                    setInProgress(false);
                })) ||
            undefined,
        scannerType,
    };
};

const DLCodeToKey: { [key: string]: keyof ILicenseScanResult; } = {
    DAA: 'LastName',
    DCS: 'LastName',
    DAB: 'LastName',
    DBO: 'LastName',
    DAC: 'FirstName',
    DBP: 'FirstName',
    DCT: 'FirstName',
    DAG: 'StreetAddress',
    DAI: 'City',
    DAJ: 'StateName',
    DAK: 'PostalCode',
};

export const useStormScanLicense = () => {
    const { setError, scannerType, ...scanner } = useStormScan();
    const [result, setResult] = useState<ILicenseScanResult>();

    const simulateResult = () => {
        const names = [
            'KRIS',
            'THOMAS',
            'RICHARD',
            'STEVENS',
            'BROWN',
            'JOHN',
            'ROBERT',
            'SHAFT',
            'LIZARD',
            'BOB',
            'ALIENS',
            'WATTENBURGER',
            'ELIZABETH',
            'VON HOMENHEIM',
            'SHEPARD',
            'NORMAN',
        ];
        const result: ILicenseScanResult = {
            FirstName: names[Math.floor(Math.random() * names.length)],
            LastName: names[Math.floor(Math.random() * names.length)],
            StreetAddress: '123 Street Road',
            City: 'Place',
            StateName: 'WY',
            PostalCode: '12345-5678',
        };
        setResult(result);
    };

    useEffect(() => {
        if (scanner.result) {
            try {
                const result: ILicenseScanResult = {
                    FirstName: '',
                    LastName: '',
                    StreetAddress: '',
                    City: '',
                    StateName: '',
                    PostalCode: '',
                };

                if (scanner.result.startsWith('%')) {
                    /*
                     * Data is formatted according to the "Optional Magnetic Strip" section of the 2020 AAMVA DL/ID Card Design Standard
                     * https://www.aamva.org/getmedia/99ac7057-0f4d-4461-b0a2-3a5532e1b35c/AAMVA-2020-DLID-Card-Design-Standard.pdf
                     */

                    const [, stateName, city, fullName, fullAddress] =
                        scanner.result.replace(/[!"&'*+,:;<=>@_]/g, '')
                            .match(/^%([A-Z]{2})([A-Z .-]{1,13})\^?([$A-Z .-]{1,35})\^?([$A-Z0-9 .-]+)\^?\?/) ?? [];
                    const [, lastName, firstName, suffix] = fullName.match(/([^$]*)\$?([^$]*)\$?([^$]*)/) ?? [];
                    const addressLines = fullAddress.split(/\s*\$\s*/g)
                        .map((i) => i.trim());
                    const postalCodeRegex = new RegExp(`${city}.*?${stateName}\\s*(.+)`);
                    const postalCode = addressLines.find((i) => postalCodeRegex.test(i))
                        ?.match(postalCodeRegex)?.[1] ?? '';
                    const streetAddress = addressLines
                        .filter((i) => !postalCodeRegex.test(i))
                        .map((i) => i.trim())
                        .join(', ');

                    result.LastName = lastName.trim();
                    result.FirstName = `${firstName.trim()}${suffix.trim().length > 0 ? ', ' : ''}${suffix.trim()}`;
                    result.StreetAddress = streetAddress.trim();
                    result.City = city.trim();
                    result.StateName = stateName.trim();
                    result.PostalCode = postalCode.trim();
                }
                else if (scannerType === 2) {
                    const rawInput = scanner.result
                        .replace(/^(data)/, '')
                        .trim()
                        .split(',');

                    if (rawInput.length > 10) {
                        result.LastName = rawInput[0];
                        result.FirstName = rawInput[1];
                        result.StreetAddress = rawInput[7];
                        result.City = rawInput[8];
                        result.StateName = rawInput[9];
                        result.PostalCode = rawInput[10];
                    }
                }
                else {
                    const rawInput = scanner.result.trim()
                        .split('\n');
                    const lines = rawInput.reduce((cleanedLines: string[], line: string) => {
                        const cleanedLine = line
                            // TODO is this even necessary?
                            // eslint-disable-next-line no-control-regex
                            .match(/[\x09\x0a\x0d\x20-\x7f]*/g)
                            ?.join('')
                            .trim();

                        if (cleanedLine) {
                            cleanedLines.push(cleanedLine);
                        }

                        return cleanedLines;
                    }, []);

                    lines.forEach((line) => {
                        const key = line.slice(0, 3);
                        let value = line.slice(3);
                        const code = DLCodeToKey[key];

                        if (code && result[code] === '') {
                            if (key === 'DAA') {
                                const [last, first] = value.split(',');
                                if (result.FirstName === '') {
                                    result.FirstName = (first ?? '').trim();
                                }
                                value = (last ?? '').trim();
                            }
                            else if (key === 'DCT') {
                                const nameParts = value.split(' ');
                                value = nameParts[0];
                            }

                            result[code] = value;
                        }
                    });
                }

                if (result.City || result.FirstName || result.LastName || result.PostalCode || result.StateName || result.StreetAddress) {
                    setResult(result);
                }
                else {
                    setError({ code: ScanErrorCode.badData });
                }
            }
            catch {
                setError({ code: ScanErrorCode.unknown });
            }
        }
    }, [scanner.result, setError, scannerType]);

    return {
        ...scanner,
        licenseScanEnabled: true,
        result,
        rawResult: scanner.result,
        simulateResult,
    };
};

export const useStormQRScanner = (saveResultAsCheckIn: boolean = true) => {
    const { setError, scannerType, ...scanner } = useStormScan();
    const [result, setResult] = useState<QRCodeData>();

    const simulateResult = (qr: IQRCodeScanResult) => {
        scanner.simulateResult && scanner.simulateResult(qr.QRCode);
    };

    React.useEffect(() => {
        if (saveResultAsCheckIn && scanner.result) {
            if (scanner.result.startsWith('{')) {
                setResult(JSON.parse(scanner.result));
            }
            else if (scanner.result.includes('|')) {
                const parsed = scanner.result.match(/[^\s|]+/g) || [];
                const apptNo = parseInt(parsed[parsed.length - 1], 10);
                if (parsed.length <= 2) {
                    // GoMotoCore QR code for DMS appt. w/ or w/o dealer key
                    const dealerKey = parsed[parsed.length - 2];
                    setResult({
                        apptNo,
                        ...(dealerKey && { dealerKey }),
                    });
                }
                else {
                    // CR+ QR code
                    const [dmsDealerKey, dmsStoreId, dmsAreaId] = parsed;
                    setResult({
                        apptNo,
                        dmsDealerKey,
                        dmsStoreId,
                        dmsAreaId,
                    });
                }
            }
            else {
                setError({ code: ScanErrorCode.badData });
            }
        }
    }, [saveResultAsCheckIn, scanner.result, setError]);

    return {
        ...scanner,
        result,
        rawResult: scanner.result,
        simulateResult,
    };
};
